import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Message, Spin, Switch, Space, Typography, Tag } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import { useOnboardingStatus, useSetProgramContext } from './hooks/useOnboardingData';
import OnboardingLayout from './OnboardingLayout';
import IdentityStep from './steps/IdentityStep';
import StudentDetailsStep from './steps/StudentDetailsStep';
import ReviewStep from './steps/ReviewStep';
import { formatMonthYear } from '@/modules/common/helpers/dateHelpers';
import './OnboardingWizard.css';

const STEP_KEYS = [
  { titleKey: 'wizard.studentSteps.personalData', descKey: 'wizard.studentSteps.personalDataDesc' },
  { titleKey: 'wizard.studentSteps.academicData', descKey: 'wizard.studentSteps.academicDataDesc' },
  { titleKey: 'wizard.studentSteps.review', descKey: 'wizard.studentSteps.reviewDesc' }
];

function StudentOnboardingWizard({ previewMode = false }) {
  const { t } = useTranslation('onboarding');

  const STEPS = useMemo(() => STEP_KEYS.map(({ titleKey, descKey }) => ({
    title: t(titleKey),
    description: t(descKey)
  })), [t]);

  const [currentStep, setCurrentStep] = useState(1);
  const [freeMode, setFreeMode] = useState(previewMode);
  const initialStepSet = useRef(false);

  // Extract launch_program_id from URL params
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const launchProgramId = searchParams.get('launch_program_id');

  // Mutation to set program context before loading status
  const setProgramContextMutation = useSetProgramContext();
  const needsProgramContext = !!launchProgramId;
  const [programContextReady, setProgramContextReady] = useState(!needsProgramContext);

  const {
    data: statusPayload,
    isLoading,
    refetch
  } = useOnboardingStatus({
    enabled: !previewMode && programContextReady
  });

  // Set program context before allowing query to run
  useEffect(() => {
    if (previewMode || !launchProgramId) return;
    setProgramContextMutation.mutate(
      { launch_program_id: parseInt(launchProgramId, 10) },
      {
        onSettled: () => {
          setProgramContextReady(true);
        }
      }
    );
  }, [launchProgramId, previewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = useMemo(() => ({
    identity: statusPayload?.identity_data || null,
    studentDetails: statusPayload?.role_specific_data || {},
    documents: statusPayload?.documents || null
  }), [statusPayload]);

  const requestedChanges = useMemo(() =>
    Array.isArray(statusPayload?.requested_changes)
      ? statusPayload.requested_changes
      : [],
  [statusPayload]);

  const programContext = useMemo(() => {
    const roleSpecific = statusPayload?.role_specific_data || {};
    if (!roleSpecific.launch_program_id) return null;
    return {
      program_name: roleSpecific.program_name,
      valor_curricular: roleSpecific.valor_curricular,
      program_type: roleSpecific.program_type,
      program_start_at: roleSpecific.program_start_at,
      schedule_name: roleSpecific.schedule_name
    };
  }, [statusPayload]);

  // Determine initial step from loaded data (runs only once)
  useEffect(() => {
    if (previewMode || !statusPayload || initialStepSet.current) return;
    initialStepSet.current = true;

    const identity = statusPayload.identity_data || {};
    const roleSpecific = statusPayload.role_specific_data || {};

    const isIdentityComplete = [
      'first_name', 'last_name', 'date_of_birth',
      'government_id', 'government_id_type',
      'phone', 'address', 'city', 'state', 'postal_code', 'country'
    ].every((field) => identity[field]);

    const hasStudentDetails = roleSpecific.highest_education_level
      && roleSpecific.degrees
      && roleSpecific.degrees.length > 0
      && roleSpecific.degree_count;

    if (!isIdentityComplete) {
      setCurrentStep(1);
    } else if (!hasStudentDetails) {
      setCurrentStep(2);
    } else {
      setCurrentStep(3);
    }
  }, [statusPayload, previewMode]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleNext = async () => {
    if (previewMode) {
      if (currentStep < STEPS.length) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo(0, 0);
      }
      return;
    }

    await refetch();

    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = async (targetStep) => {
    if (previewMode) {
      if (typeof targetStep === 'number') {
        setCurrentStep(targetStep);
      } else {
        setCurrentStep(prev => Math.max(1, prev - 1));
      }
      window.scrollTo(0, 0);
      return;
    }

    await refetch();

    if (typeof targetStep === 'number') {
      setCurrentStep(targetStep);
    } else {
      setCurrentStep(prev => Math.max(1, prev - 1));
    }
    window.scrollTo(0, 0);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <IdentityStep
            key={`identity-${JSON.stringify(data.identity)?.substring(0, 50)}`}
            onNext={handleNext}
            initialData={data.identity}
            documents={data.documents}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );

      case 2:
        return (
          <StudentDetailsStep
            key={`student-${JSON.stringify(data.studentDetails)?.substring(0, 50)}`}
            onNext={handleNext}
            onBack={handleBack}
            initialData={data.studentDetails}
            documents={data.documents}
            programContext={programContext}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );

      case 3:
        return (
          <ReviewStep
            key={`review-${JSON.stringify(data)?.substring(0, 50)}`}
            onBack={handleBack}
            data={{
              identity: data.identity,
              teacherDetails: data.studentDetails,
              documents: data.documents
            }}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );

      default:
        return null;
    }
  };

  if (isLoading || setProgramContextMutation.isPending) {
    return (
      <OnboardingLayout>
        <div className="onboarding-loading">
          <Spin size={40} />
          <p>{t('wizard.loading')}</p>
        </div>
      </OnboardingLayout>
    );
  }

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
    window.scrollTo(0, 0);
  };

  return (
    <OnboardingLayout
      currentStep={currentStep}
      totalSteps={STEPS.length}
      stepTitle={STEPS[currentStep - 1]?.title || ''}
      onStepClick={handleStepClick}
      steps={STEPS}
    >
      <div className="onboarding-wizard">
        <div className="wizard-content">
          {previewMode && (
            <div style={{ marginBottom: 16 }}>
              <Message
                type="warning"
                content={t('wizard.simulationMode')}
                style={{ marginBottom: 12 }}
              />
              <Space align="center">
                <Switch checked={freeMode} onChange={setFreeMode} />
                <Typography.Text>{t('wizard.freeMode')}</Typography.Text>
              </Space>
            </div>
          )}

          {programContext && (
            <div style={{
              background: '#f0f5ff',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 16,
              border: '1px solid #adc6ff'
            }}>
              <Space size="medium" wrap>
                {programContext.program_name && (
                  <Typography.Text bold>{programContext.program_name}</Typography.Text>
                )}
                {programContext.valor_curricular !== undefined && (
                  <Tag color={programContext.valor_curricular ? 'green' : 'gray'}>
                    {programContext.valor_curricular ? t('wizard.withCurricularValue') : t('wizard.withoutCurricularValue')}
                  </Tag>
                )}
                {programContext.schedule_name && (
                  <Tag color="arcoblue">{programContext.schedule_name}</Tag>
                )}
                {programContext.program_start_at && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('wizard.startDate')}: {formatMonthYear(programContext.program_start_at)}
                  </Typography.Text>
                )}
              </Space>
            </div>
          )}

          {renderStep()}
        </div>
      </div>
    </OnboardingLayout>
  );
}

export default StudentOnboardingWizard;
